export const endpoints = (api) => ({
    auth: {
        me: () => api.get('/auth/me'),
        changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
    },
    dashboard: {
        summary: (query) => api.get('/dashboard', query),
        search: (q) => api.get('/dashboard/search', { q }),
    },
    /** Live reference rates for the "View in INR" display; never stored. */
    fx: {
        inrRates: () => api.get('/fx/inr-rates'),
    },
    invoices: {
        list: (query) => api.get('/invoices', query),
        get: (id) => api.get(`/invoices/${id}`),
        documentUrl: (id) => `/invoices/${id}/document`,
        upload: (file, companyId, fileName, rawOcr) => {
            const form = new FormData();
            form.append('file', file, fileName);
            form.append('companyId', companyId);
            // Multipart fields are strings, so the browser's OCR result travels as JSON.
            if (rawOcr)
                form.append('rawOcr', JSON.stringify(rawOcr));
            return api.upload('/invoices/upload', form);
        },
        update: (id, body) => api.patch(`/invoices/${id}`, body),
        resolveFinding: (id, body) => api.post(`/invoices/${id}/findings/resolve`, body),
        submit: (id) => api.post(`/invoices/${id}/submit`),
        cancel: (id, reason) => api.post(`/invoices/${id}/cancel`, { reason }),
        /** Soft-deletes an invoice not yet submitted for approval; it is hidden from then on. */
        delete: (id, reason) => api.post(`/invoices/${id}/delete`, { reason }),
        reextract: (id) => api.post(`/invoices/${id}/reextract`),
        /** Fetches one PO from the Elixir API by its number; `purchaseOrder` is null when unknown. */
        lookupPurchaseOrder: (id, poNumber) => api.post(`/invoices/${id}/purchase-order/lookup`, { poNumber }),
        /** Matches the invoice to a PO by its number. Optional. */
        linkPurchaseOrder: (id, poNumber) => api.put(`/invoices/${id}/purchase-order`, { poNumber }),
        unlinkPurchaseOrder: (id) => api.delete(`/invoices/${id}/purchase-order`),
        /**
         * The accounting stage: clear for payment, escalate to Treasury, or
         * return for correction. `netPayable` is derived by the server, so it is
         * deliberately not part of the body.
         */
        verify: (id, body) => api.post(`/invoices/${id}/verify`, body),
        dueReminders: (query) => api.get('/invoices/due-reminders', query),
    },
    /** The Invoice Mailbox: a user's own Outlook, and what it has pulled. */
    mail: {
        /** Resolves to null when the user has not connected a mailbox yet. */
        connection: () => api.get('/integrations/outlook/connection'),
        /**
         * Returns a URL rather than redirecting: the browser must navigate to
         * Microsoft itself, and this client cannot hand off its bearer token.
         */
        connect: (defaultCompanyId) => api.post('/integrations/outlook/authorize', { defaultCompanyId }),
        disconnect: () => api.delete('/integrations/outlook/connection'),
        updateConnection: (body) => api.patch('/integrations/outlook/connection', body),
        /** 202: the run continues in the background and the screen polls for it. */
        sync: () => api.post('/integrations/outlook/sync'),
        ingestions: (query) => api.get('/integrations/outlook/ingestions', query),
        ingestion: (id) => api.get(`/integrations/outlook/ingestions/${id}`),
        retry: (id) => api.post(`/integrations/outlook/ingestions/${id}/retry`),
    },
    approvals: {
        list: (query) => api.get('/approvals', query),
        get: (id) => api.get(`/approvals/${id}`),
        act: (id, action, comment) => api.post(`/approvals/${id}/act`, { action, comment }),
    },
    /** Treasury escalations raised by finance on a single invoice. */
    financeRequests: {
        list: (query) => api.get('/finance-requests', query),
        get: (id) => api.get(`/finance-requests/${id}`),
        act: (id, action, remarks) => api.post(`/finance-requests/${id}/act`, {
            action,
            remarks,
        }),
        addRemark: (id, remarks) => api.post(`/finance-requests/${id}/remarks`, { remarks }),
        setPriority: (id, priority) => api.patch(`/finance-requests/${id}/priority`, { priority }),
    },
    payables: {
        /** Invoices, and loan EMIs and charges shaped like them (`payableOf: 'LOAN'`). */
        list: (query) => api.get('/payables', query),
        summary: (query) => api.get('/payables/summary', query),
        ageing: (query) => api.get('/payables/ageing', query),
    },
    /** Loans the company has taken, under Settings → Payment Accounts → Loans. */
    loans: {
        list: (query) => api.get('/loans', query),
        get: (id) => api.get(`/loans/${id}`),
        preview: (body) => api.post('/loans/preview', body),
        create: (body) => api.post('/loans', body),
        update: (id, body) => api.patch(`/loans/${id}`, body),
        cancel: (id) => api.delete(`/loans/${id}`),
        activate: (id) => api.post(`/loans/${id}/activate`),
        /** The lender's schedule from .xlsx or .csv; a dry run checks it and writes nothing. */
        importSchedule: (id, file, dryRun) => {
            const form = new FormData();
            form.append('file', file, file.name);
            return api.upload(`/loans/${id}/schedule/import?dryRun=${dryRun ? 'true' : 'false'}`, form);
        },
        editInstalment: (id, instalmentId, body) => api.patch(`/loans/${id}/instalments/${instalmentId}`, body),
        addCharge: (id, body) => api.post(`/loans/${id}/charges`, body),
        dueReminders: (query) => api.get('/loans/due-reminders', query),
    },
    /** HR → Employees: the employee master, kept by HR. */
    hr: {
        employees: (query) => api.get('/hr/employees', query),
        employee: (id) => api.get(`/hr/employees/${id}`),
        createEmployee: (body) => api.post('/hr/employees', body),
        updateEmployee: (id, body) => api.patch(`/hr/employees/${id}`, body),
        projects: (query) => api.get('/hr/projects', query),
        createProject: (body) => api.post('/hr/projects', body),
        updateProject: (id, body) => api.patch(`/hr/projects/${id}`, body),
        projectEmployees: (id) => api.get(`/hr/projects/${id}/employees`),
        updateProjectEmployees: (id, employeeIds) => api.put(`/hr/projects/${id}/employees`, { employeeIds }),
        /** An employee list from .xlsx or .csv; a dry run checks it and writes nothing. */
        importEmployees: (input) => {
            const form = new FormData();
            form.append('file', input.file);
            const query = new URLSearchParams({
                companyId: input.companyId,
                dryRun: String(input.dryRun),
            });
            return api.upload(`/hr/employees/import?${query}`, form);
        },
    },
    payroll: {
        list: (query) => api.get('/payroll', query),
        get: (id) => api.get(`/payroll/${id}`),
        employees: (id, query) => api.get(`/payroll/${id}/employees`, query),
        preview: (file, mapping, fileName) => {
            const form = new FormData();
            form.append('file', file, fileName);
            if (mapping)
                form.append('mapping', JSON.stringify(mapping));
            return api.upload('/payroll/preview', form);
        },
        import: (input) => {
            const form = new FormData();
            form.append('file', input.file, input.fileName);
            form.append('companyId', input.companyId);
            form.append('label', input.label);
            form.append('periodMonth', String(input.periodMonth));
            form.append('periodYear', String(input.periodYear));
            if (input.mapping)
                form.append('mapping', JSON.stringify(input.mapping));
            return api.upload('/payroll/import', form);
        },
        submit: (id) => api.post(`/payroll/${id}/submit`),
        cancel: (id) => api.delete(`/payroll/${id}`),
    },
    payments: {
        queue: (query) => api.get('/payments/queue', query),
        hold: (id, hold, reason) => api.post(`/payments/queue/${id}/hold`, { hold, reason }),
        batches: (query) => api.get('/payments/batches', query),
        batch: (id) => api.get(`/payments/batches/${id}`),
        createBatch: (body) => api.post('/payments/batches', body),
        updateBatch: (id, body) => api.patch(`/payments/batches/${id}`, body),
        exportBatch: (id) => api.post(`/payments/batches/${id}/export`),
        downloadFile: (id) => api.download(`/payments/batches/${id}/file`),
    },
    banking: {
        statements: (query) => api.get('/banking/statements', query),
        uploadStatement: (input) => {
            const form = new FormData();
            form.append('file', input.file, input.fileName);
            form.append('companyId', input.companyId);
            form.append('bankAccountId', input.bankAccountId);
            if (input.mapping)
                form.append('mapping', JSON.stringify(input.mapping));
            return api.upload('/banking/statements', form);
        },
        deleteStatement: (id) => api.delete(`/banking/statements/${id}`),
        transactions: (query) => api.get('/banking/transactions', query),
    },
    reconciliation: {
        list: (query) => api.get('/reconciliation', query),
        summary: (query) => api.get('/reconciliation/summary', query),
        candidates: (transactionId) => api.get(`/reconciliation/transactions/${transactionId}/candidates`),
        confirm: (bankTransactionId, obligationId, note) => api.post('/reconciliation/confirm', {
            bankTransactionId,
            obligationId,
            note,
        }),
        ignore: (bankTransactionId, note) => api.post('/reconciliation/ignore', { bankTransactionId, note }),
        unmatch: (reconciliationId, note) => api.post('/reconciliation/unmatch', { reconciliationId, note }),
    },
    reports: {
        catalogue: () => api.get('/reports'),
        run: (key, query) => api.get(`/reports/${key}`, query),
        download: (key, query) => api.download(`/reports/${key}`, { ...query, format: 'xlsx' }),
    },
    audit: {
        list: (query) => api.get('/audit', query),
        forEntity: (entityType, entityId) => api.get(`/audit/entity/${entityType}/${entityId}`),
    },
    notifications: {
        list: (query) => api.get('/notifications', query),
        unreadCount: () => api.get('/notifications/unread-count'),
        markRead: (id) => api.post(`/notifications/${id}/read`),
        markAllRead: () => api.post('/notifications/read-all'),
    },
    // A vendor's own login: the invoices it billed, across the companies it bills.
    vendorPortal: {
        companies: () => api.get('/vendor-portal/companies'),
        invoices: (query) => api.get('/vendor-portal/invoices', query),
    },
    settings: {
        companies: (query) => api.get('/settings/companies', query),
        company: (id) => api.get(`/settings/companies/${id}`),
        createCompany: (body) => api.post('/settings/companies', body),
        updateCompany: (id, body) => api.patch(`/settings/companies/${id}`, body),
        deleteCompany: (id) => api.delete(`/settings/companies/${id}`),
        /** System Settings: only the switches sent change. */
        updateCompanySettings: (id, body) => api.patch(`/settings/companies/${id}/settings`, body),
        groups: (query) => api.get('/settings/groups', query),
        createGroup: (body) => api.post('/settings/groups', body),
        updateGroup: (id, body) => api.patch(`/settings/groups/${id}`, body),
        deleteGroup: (id) => api.delete(`/settings/groups/${id}`),
        regions: (query) => api.get('/settings/regions', query),
        createRegion: (body) => api.post('/settings/regions', body),
        updateRegion: (id, body) => api.patch(`/settings/regions/${id}`, body),
        deleteRegion: (id) => api.delete(`/settings/regions/${id}`),
        verticals: (query) => api.get('/settings/verticals', query),
        createVertical: (body) => api.post('/settings/verticals', body),
        updateVertical: (id, body) => api.patch(`/settings/verticals/${id}`, body),
        deleteVertical: (id) => api.delete(`/settings/verticals/${id}`),
        businessUnits: (query) => api.get('/settings/business-units', query),
        createBusinessUnit: (body) => api.post('/settings/business-units', body),
        updateBusinessUnit: (id, body) => api.patch(`/settings/business-units/${id}`, body),
        deleteBusinessUnit: (id) => api.delete(`/settings/business-units/${id}`),
        locations: (query) => api.get('/settings/locations', query),
        createLocation: (body) => api.post('/settings/locations', body),
        updateLocation: (id, body) => api.patch(`/settings/locations/${id}`, body),
        deleteLocation: (id) => api.delete(`/settings/locations/${id}`),
        departments: (query) => api.get('/settings/departments', query),
        createDepartment: (body) => api.post('/settings/departments', body),
        updateDepartment: (id, body) => api.patch(`/settings/departments/${id}`, body),
        deleteDepartment: (id) => api.delete(`/settings/departments/${id}`),
        vendors: (query) => api.get('/settings/vendors', query),
        vendor: (id) => api.get(`/settings/vendors/${id}`),
        createVendor: (body) => api.post('/settings/vendors', body),
        updateVendor: (id, body) => api.patch(`/settings/vendors/${id}`, body),
        deleteVendor: (id) => api.delete(`/settings/vendors/${id}`),
        vendorAdvance: (id) => api.get(`/settings/vendors/${id}/advance`),
        addVendorAdvance: (id, body) => api.post(`/settings/vendors/${id}/advance`, body),
        reverseVendorAdvance: (id, entryId, body) => api.post(`/settings/vendors/${id}/advance/${entryId}/reverse`, body),
        users: (query) => api.get('/settings/users', query),
        createUser: (body) => api.post('/settings/users', body),
        reinviteUser: (id) => api.post(`/settings/users/${id}/reinvite`),
        updateUser: (id, body) => api.patch(`/settings/users/${id}`, body),
        deleteUser: (id) => api.delete(`/settings/users/${id}`),
        roles: () => api.get('/settings/roles'),
        createRole: (body) => api.post('/settings/roles', body),
        updateRole: (id, body) => api.patch(`/settings/roles/${id}`, body),
        deleteRole: (id) => api.delete(`/settings/roles/${id}`),
        bankAccounts: (query) => api.get('/settings/bank-accounts', query),
        createBankAccount: (body) => api.post('/settings/bank-accounts', body),
        updateBankAccount: (id, body) => api.patch(`/settings/bank-accounts/${id}`, body),
        deleteBankAccount: (id) => api.delete(`/settings/bank-accounts/${id}`),
        paymentCards: (query) => api.get('/settings/payment-cards', query),
        createPaymentCard: (body) => api.post('/settings/payment-cards', body),
        updatePaymentCard: (id, body) => api.patch(`/settings/payment-cards/${id}`, body),
        deletePaymentCard: (id) => api.delete(`/settings/payment-cards/${id}`),
        netBanking: (query) => api.get('/settings/net-banking', query),
        createNetBanking: (body) => api.post('/settings/net-banking', body),
        updateNetBanking: (id, body) => api.patch(`/settings/net-banking/${id}`, body),
        deleteNetBanking: (id) => api.delete(`/settings/net-banking/${id}`),
        ledgers: (query) => api.get('/settings/ledgers', query),
        createLedger: (body) => api.post('/settings/ledgers', body),
        updateLedger: (id, body) => api.patch(`/settings/ledgers/${id}`, body),
        deleteLedger: (id) => api.delete(`/settings/ledgers/${id}`),
        /** The tenant's TDS sections and their rates, shared by all its companies. */
        tdsSections: (query) => api.get('/settings/tds-sections', query),
        createTdsSection: (body) => api.post('/settings/tds-sections', body),
        updateTdsSection: (id, body) => api.patch(`/settings/tds-sections/${id}`, body),
        deleteTdsSection: (id) => api.delete(`/settings/tds-sections/${id}`),
        /** A new code taking a section over, e.g. 194IC for 194IB; the old one is kept read-only. */
        replaceTdsSection: (id, body) => api.post(`/settings/tds-sections/${id}/replace`, body),
        /** A chart of accounts from .xlsx or .csv; a dry run checks it and writes nothing. */
        importLedgers: (input) => {
            const form = new FormData();
            form.append('file', input.file);
            const query = new URLSearchParams({
                companyId: input.companyId,
                dryRun: String(input.dryRun),
            });
            return api.upload(`/settings/ledgers/import?${query}`, form);
        },
        approvalRules: (query) => api.get('/settings/approval-rules', query),
        createApprovalRule: (body) => api.post('/settings/approval-rules', body),
        updateApprovalRule: (id, body) => api.patch(`/settings/approval-rules/${id}`, body),
        deleteApprovalRule: (id) => api.delete(`/settings/approval-rules/${id}`),
        simulateApprovalRule: (body) => api.post('/settings/approval-rules/simulate', body),
    },
});
//# sourceMappingURL=endpoints.js.map